import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ArrowRight, Package } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOrder } from "../context/OrderContext";
import ProductCard from "../components/order/ProductCard";
import { Input } from "../components/ui/input";
import { EmptyState } from "../components/Shared";

export function OrderBar() {
  const { count, loaded } = useOrder();
  const navigate = useNavigate();
  if (!loaded || !count) return null;
  return (
    <div className="fixed bottom-[96px] lg:bottom-6 inset-x-0 lg:left-[260px] z-30 px-4 pointer-events-none">
      <button onClick={() => navigate("/order/review")} className="pointer-events-auto mx-auto w-full max-w-lg h-14 rounded-full bg-slate-950 text-white shadow-xl flex items-center justify-between px-5 active:scale-[0.99] transition-transform" data-testid="review-order-bar">
        <span className="font-semibold" data-testid="order-item-count">{Number.isInteger(count) ? count : count.toFixed(1)} item{count === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-2 font-semibold text-emerald-300">Review Order <ArrowRight className="h-5 w-5" /></span>
      </button>
    </div>
  );
}

export default function Order() {
  const { restaurantId, restaurants } = useAuth();
  const { items, loaded } = useOrder();
  const quick = useLocation().pathname.endsWith("/quick");
  const [groups, setGroups] = useState(null);
  const [q, setQ] = useState("");
  const restaurant = restaurants.find((r) => r.id === restaurantId);

  const reload = useCallback(() => {
    if (!restaurantId) return;
    api.get("/ordering/catalog", { params: { restaurant_id: restaurantId } }).then((r) => setGroups(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [restaurantId]);
  useEffect(() => { reload(); }, [reload]);

  const sections = useMemo(() => {
    if (!groups) return [];
    const ql = q.trim().toLowerCase();
    if (ql) {
      const words = ql.split(/\s+/).filter(Boolean);
      const nameHit = (g) => { const names = [g.name, ...g.options.map((o) => o.description)].join(" ").toLowerCase(); return words.every((w) => names.includes(w)); };
      const byName = groups.filter(nameHit);
      if (byName.length) return [["Results", byName]];
      // nothing by product name → fall back to supplier name (e.g. typing "metro")
      return [["Results", groups.filter((g) => g.options.some((o) => words.every((w) => o.supplier_name.toLowerCase().includes(w))))]];
    }
    const frequent = groups.filter((g) => g.frequency > 0).slice(0, 12);
    if (quick) return [["Quick Order", frequent.length ? frequent : groups.slice(0, 12)]];
    const recent = groups.filter((g) => g.last_ordered_at).sort((a, b) => b.last_ordered_at.localeCompare(a.last_ordered_at)).slice(0, 6);
    return [["Frequently Ordered", frequent], ["Recent Products", recent], ["All Products", groups]].filter(([, l]) => l.length);
  }, [groups, q, quick]);

  return (
    <div data-testid={quick ? "quick-order-page" : "order-page"} className="max-w-3xl pb-28">
      <div className="sticky top-[60px] lg:top-0 z-20 -mx-4 px-4 pt-1 pb-3 bg-slate-50/95 backdrop-blur">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{quick ? "Quick Order" : "New Order"}</h1>
          <span className="text-xs text-slate-500" data-testid="ordering-for">Ordering for: <b className="text-slate-800">{restaurant?.name}</b></span>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1"><Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="h-12 pl-9 text-base" data-testid="product-search" /></div>
          <Link to={quick ? "/order" : "/order/quick"} className="h-12 px-3 rounded-md border border-slate-200 bg-white text-sm font-medium grid place-items-center" data-testid="toggle-quick">{quick ? "All" : "Quick"}</Link>
        </div>
      </div>

      {!groups || !loaded ? <p className="text-sm text-slate-500 mt-4">Loading products…</p> : groups.length === 0 ? (
        <EmptyState icon={Package} title="No products yet" hint="Products appear here once invoices have been scanned and confirmed." testId="order-empty" />
      ) : sections.map(([title, list]) => (
        <ProductSection key={title} title={title} list={list} reload={reload} />
      ))}
      <OrderBar />
      {Object.keys(items).length > 0 && null}
    </div>
  );
}

// "All Products" can run into hundreds of rows; rendering every ProductCard upfront pegs mobile main
// threads for several seconds. We mount in chunks of 20 and grow the window as a sentinel scrolls into
// view, so the first paint is bounded and the rest streams in on demand. Smaller sections (Frequently
// Ordered, Recent, filtered Results) stay fully rendered because they're already capped.
const CHUNK = 20;
function ProductSection({ title, list, reload }) {
  const isLong = list.length > CHUNK;
  const [limit, setLimit] = useState(isLong ? CHUNK : list.length);
  const sentinel = useRef(null);
  // Shrink/grow back to the window size when the underlying list changes (search → All Products).
  useEffect(() => { setLimit(list.length > CHUNK ? CHUNK : list.length); }, [list]);
  useEffect(() => {
    if (!isLong || limit >= list.length || !sentinel.current) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setLimit((n) => Math.min(n + CHUNK, list.length)); }, { rootMargin: "600px 0px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [isLong, limit, list.length]);
  const shown = list.slice(0, limit);
  return (
    <section className="mt-4" data-testid={`section-${title.toLowerCase().replace(/ /g, "-")}`}>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title} <span className="text-slate-400 font-normal">({list.length})</span></h2>
      <div className="space-y-2">{shown.map((g) => <ProductCard key={`${title}-${g.id}`} group={g} onChanged={reload} />)}</div>
      {list.length === 0 && <p className="text-sm text-slate-500">No matches.</p>}
      {limit < list.length && (
        <div ref={sentinel} className="mt-3 flex items-center justify-center" data-testid={`load-more-${title.toLowerCase().replace(/ /g, "-")}`}>
          <button type="button" onClick={() => setLimit((n) => Math.min(n + CHUNK, list.length))} className="text-xs font-medium text-slate-500 hover:text-slate-800 py-2">Loading more… ({limit}/{list.length})</button>
        </div>
      )}
    </section>
  );
}
